import { ErrorModal } from "@dexteel/mesf-core";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { get } from "lodash-es";
import { useEffect, useRef, useState } from "react";
import { FieldError } from "react-hook-form";
import { getSampleTypes } from "../repositories/SampleRepository";

interface SampleType {
  SampleTypeId: number;
  SampleTypeName: string;
}

interface SampleSelectorProps {
  value: number | null;
  onChange: (value: number | null, sampleTypeName: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  disabled?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  fieldError?: FieldError | undefined;
}

export const SampleSelector = ({
  value,
  onChange,
  onBlur = () => {},
  onFocus = () => {},
  disabled = false,
  onOpen = () => {},
  onClose = () => {},
  fieldError,
}: SampleSelectorProps) => {
  const [samples, setSamples] = useState<SampleType[]>([]);
  const [selectedSample, setSelectedSample] = useState<SampleType | null>(null);
  const filteredSamplesRef = useRef<SampleType[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const loadData = async () => {
    try {
      setIsLoading(true);
      const resp = await getSampleTypes();
      if (resp.ok) {
        const rows = get(resp, "data.tables[0].rows", []) as SampleType[];
        setSamples(rows);
      } else {
        setError(resp.message ?? "Error loading samples");
      }
    } catch (error) {
      setError("Error loading samples");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (samples.length > 0 && value) {
      const sample = samples.find((s) => s.SampleTypeId === value);
      setSelectedSample(sample || null);
    } else {
      setSelectedSample(null);
    }
  }, [value, samples]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (filteredSamplesRef.current.length > 0) {
        const firstSample = filteredSamplesRef.current[0];
        setSelectedSample(firstSample);
        if (firstSample?.SampleTypeId) {
          onChange(firstSample.SampleTypeId, firstSample.SampleTypeName);
        }
        inputRef.current?.blur();
      }
    }
  };

  return (
    <>
      <Autocomplete
        id="sample-selector"
        clearOnEscape
        options={samples || []}
        getOptionLabel={(option) => option.SampleTypeName || ""}
        value={selectedSample}
        onChange={(event, newValue) => {
          setSelectedSample(newValue);
          if (newValue?.SampleTypeId) {
            onChange(newValue.SampleTypeId, newValue.SampleTypeName);
          } else {
            onChange(null, "");
          }
        }}
        onOpen={onOpen}
        onClose={onClose}
        onInputChange={(event, value, reason) => {
          if (reason === "input") {
            filteredSamplesRef.current = samples.filter((sample) =>
              sample.SampleTypeName?.toLowerCase().includes(
                value.toLowerCase(),
              ),
            );
          }
        }}
        isOptionEqualToValue={(option, value) =>
          option.SampleTypeId === value.SampleTypeId
        }
        disabled={disabled || isLoading}
        loading={isLoading}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={inputRef}
            label="Sample Code"
            variant="outlined"
            fullWidth
            onKeyDown={handleKeyDown}
            onBlur={onBlur}
            onFocus={onFocus}
            error={!!fieldError}
            helperText={fieldError?.message}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />
      <ErrorModal error={error} onHide={() => setError("")} />
    </>
  );
};
